import type { ReactNode } from "react";
import {
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Checkmark12Regular, ChevronDown12Regular } from "@fluentui/react-icons";
import { StatusWord } from "@/components/visual/ink-report";
import type { WeeklyTone } from "@/features/weekly-report/weekly-report-model";

const useStyles = makeStyles({
  badgeTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    background: "none",
    border: "none",
    padding: "1px 2px",
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: tokens.fontWeightRegular,
    color: "inherit",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "@media print": {
      padding: 0,
      margin: 0,
      backgroundColor: "transparent !important",
      cursor: "default !important",
    },
  },
  readOnlyBadge: {
    display: "inline-flex",
    alignItems: "center",
  },
  value: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  caret: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    "@media print": {
      display: "none !important",
    },
  },
  check: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
  },
});

export type BadgeTone = "good" | "watch" | "critical" | "risk" | "brand" | "neutral" | "quiet";

export interface PopoverOption<T> {
  value: T;
  label: string;
  tone?: BadgeTone;
  icon?: ReactNode;
}

export interface InlinePopoverBadgeProps<T> {
  value: T;
  options: PopoverOption<T>[];
  onChange: (newValue: T) => void;
  readOnly?: boolean;
  ariaLabel?: string;
  className?: string;
  renderBadge?: (option: PopoverOption<T> | undefined) => ReactNode;
}

function wordTone(tone?: BadgeTone): WeeklyTone {
  if (tone === "good") return "good";
  if (tone === "watch") return "watch";
  if (tone === "critical" || tone === "risk") return "risk";
  if (tone === "quiet") return "quiet";
  return "neutral";
}

export function InlinePopoverBadge<T extends string | number | null>({
  value,
  options,
  onChange,
  readOnly = false,
  ariaLabel,
  className,
  renderBadge,
}: InlinePopoverBadgeProps<T>) {
  const styles = useStyles();
  const currentOption = options.find((opt) => opt.value === value) ?? options[0];

  const optionBody = (option: PopoverOption<T> | undefined) =>
    option ? (
      <span className={styles.value}>
        {option.icon}
        <StatusWord tone={wordTone(option.tone)}>{option.label}</StatusWord>
      </span>
    ) : (
      <StatusWord tone="quiet">—</StatusWord>
    );

  const badgeContent = renderBadge ? renderBadge(currentOption) : optionBody(currentOption);

  if (readOnly) {
    return <span className={mergeClasses(styles.readOnlyBadge, className)}>{badgeContent}</span>;
  }

  return (
    <Menu positioning="below-start">
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={mergeClasses(styles.badgeTrigger, className)}
          aria-label={ariaLabel || currentOption?.label}
        >
          {badgeContent}
          <span className={styles.caret} data-print-hide="">
            <ChevronDown12Regular />
          </span>
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <MenuItem key={String(opt.value)} onClick={() => onChange(opt.value)}>
                {optionBody(opt)}
                {isSelected ? (
                  <span className={styles.check}>
                    <Checkmark12Regular />
                  </span>
                ) : null}
              </MenuItem>
            );
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
